import { create } from "zustand";
import { message } from "antd";

import AdminApiCaller from "../config/api/adminapicaller";
import { readCache, writeCache } from "./cache";

const LIST_TTL = 5 * 60 * 1000;

const cacheStore = {
  authors: { data: null, expiresAt: 0 },
  categories: { data: null, expiresAt: 0 },
};

const initialState = {
  authors: [],
  authorsCount: 0,
  author: {},
  isAuthorLoading: false,
  categories: [],
  categoriesCount: 0,
  category: {},
  isCategoryLoading: false,
  isFileLoading: false,
  error: null,
};

export const useOthersStore = create((set) => {
  const setOthers = (partial) => set((state) => ({ ...state, ...partial }));

  return {
    ...initialState,
    addAuthor: async (payload, cb) => {
      try {
        setOthers({ isAuthorLoading: true, error: null });
        const res = await AdminApiCaller.Post("/add-author", payload);
        if (res?.status === 200) {
          message.success(res.data.message);
          cacheStore.authors.expiresAt = 0;
        } else {
          message.error(res.data.message);
        }
        setOthers({ isAuthorLoading: false });
        if (cb) cb(res?.data);
      } catch (err) {
        setOthers({ isAuthorLoading: false, error: err });
      }
    },
    getAuthors: async (cb) => {
      try {
        setOthers({ isAuthorLoading: true, error: null });
        const cached = readCache(cacheStore.authors);
        if (cached) {
          setOthers({
            authors: cached.authors,
            authorsCount: cached.count,
            isAuthorLoading: false,
          });
          if (cb) cb(cached);
          return;
        }
        const res = await AdminApiCaller.Get("/get-authors", {}, {
          cache: true,
          ttlMs: LIST_TTL,
        });
        if (res?.status === 200) {
          setOthers({
            authors: res.data.authors,
            authorsCount: res.data.count,
            isAuthorLoading: false,
          });
          writeCache(cacheStore.authors, res.data, LIST_TTL);
        } else {
          setOthers({ isAuthorLoading: false });
        }
        if (cb) cb(res?.data);
      } catch (err) {
        setOthers({ isAuthorLoading: false, error: err });
      }
    },
    getAuthor: async (payload, cb) => {
      try {
        setOthers({ isAuthorLoading: true, error: null });
        const res = await AdminApiCaller.Get(`/get-author?id=${payload?.id}`);
        if (res?.status === 200) {
          setOthers({ author: res.data.author, isAuthorLoading: false });
        } else {
          setOthers({ isAuthorLoading: false });
        }
        if (cb) cb(res?.data);
      } catch (err) {
        setOthers({ isAuthorLoading: false, error: err });
      }
    },
    updateAuthor: async (payload, cb) => {
      try {
        setOthers({ isAuthorLoading: true, error: null });
        const res = await AdminApiCaller.Put("/update-author", payload);
        if (res?.status === 200) {
          message.success(res.data.message);
          cacheStore.authors.expiresAt = 0;
        } else {
          message.error(res.data.message);
        }
        setOthers({ isAuthorLoading: false });
        if (cb) cb(res?.data);
      } catch (err) {
        setOthers({ isAuthorLoading: false, error: err });
      }
    },
    deleteAuthor: async (payload, cb) => {
      try {
        setOthers({ isAuthorLoading: true, error: null });
        const res = await AdminApiCaller.Delete(`/delete-author?id=${payload.id}`);
        if (res?.status === 200) {
          message.success(res.data.message);
          cacheStore.authors.expiresAt = 0;
        } else {
          message.error(res.data.message);
        }
        setOthers({ isAuthorLoading: false });
        if (cb) cb(res?.data);
      } catch (err) {
        setOthers({ isAuthorLoading: false, error: err });
      }
    },
    addCategory: async (payload, cb) => {
      try {
        setOthers({ isCategoryLoading: true, error: null });
        const res = await AdminApiCaller.Post("/add-category", payload);
        if (res?.status === 200) {
          message.success(res.data.message);
          cacheStore.categories.expiresAt = 0;
        } else {
          message.error(res.data.message);
        }
        setOthers({ isCategoryLoading: false });
        if (cb) cb(res?.data);
      } catch (err) {
        setOthers({ isCategoryLoading: false, error: err });
      }
    },
    getCategories: async (cb) => {
      try {
        setOthers({ isCategoryLoading: true, error: null });
        const cached = readCache(cacheStore.categories);
        if (cached) {
          setOthers({
            categories: cached.categories,
            categoriesCount: cached.count,
            isCategoryLoading: false,
          });
          if (cb) cb(cached);
          return;
        }
        const res = await AdminApiCaller.Get("/get-categories", {}, {
          cache: true,
          ttlMs: LIST_TTL,
        });
        if (res?.status === 200) {
          setOthers({
            categories: res.data.categories,
            categoriesCount: res.data.count,
            isCategoryLoading: false,
          });
          writeCache(cacheStore.categories, res.data, LIST_TTL);
        } else {
          setOthers({ isCategoryLoading: false });
        }
        if (cb) cb(res?.data);
      } catch (err) {
        setOthers({ isCategoryLoading: false, error: err });
      }
    },
    getCategory: async (payload, cb) => {
      try {
        setOthers({ isCategoryLoading: true, error: null });
        const res = await AdminApiCaller.Get(`/get-category?id=${payload?.id}`);
        if (res?.status === 200) {
          setOthers({ category: res.data.category, isCategoryLoading: false });
        } else {
          setOthers({ isCategoryLoading: false });
        }
        if (cb) cb(res?.data);
      } catch (err) {
        setOthers({ isCategoryLoading: false, error: err });
      }
    },
    updateCategory: async (payload, cb) => {
      try {
        setOthers({ isCategoryLoading: true, error: null });
        const res = await AdminApiCaller.Put("/update-category", payload);
        if (res?.status === 200) {
          message.success(res.data.message);
          cacheStore.categories.expiresAt = 0;
        } else {
          message.error(res.data.message);
        }
        setOthers({ isCategoryLoading: false });
        if (cb) cb(res?.data);
      } catch (err) {
        setOthers({ isCategoryLoading: false, error: err });
      }
    },
    deleteCategory: async (payload, cb) => {
      try {
        setOthers({ isCategoryLoading: true, error: null });
        const res = await AdminApiCaller.Delete(`/delete-category?id=${payload.id}`);
        if (res?.status === 200) {
          message.success(res.data.message);
          cacheStore.categories.expiresAt = 0;
        } else {
          message.error(res.data.message);
        }
        setOthers({ isCategoryLoading: false });
        if (cb) cb(res?.data);
      } catch (err) {
        setOthers({ isCategoryLoading: false, error: err });
      }
    },
    uploadFile: async (payload, cb) => {
      try {
        setOthers({ isFileLoading: true, error: null });
        const res = await AdminApiCaller.Post("/upload-blog-image", payload);
        if (res?.status === 200) {
          message.success(res.data.message);
        } else {
          message.error(res.data.message);
        }
        setOthers({ isFileLoading: false });
        if (cb) cb(res?.data);
      } catch (err) {
        setOthers({ isFileLoading: false, error: err });
      }
    },
    reset: () => set(initialState),
  };
});
